import type { FC, FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import 'swiper/css'
import { Button } from '~/components/common/Button'
import { EmailInput } from '~/components/common/EmailInput'
import { IconCrossFilled } from '~/components/icons'
import { Link } from '~/components/common/Link'
import { useAppConfig } from '~/contexts/AppConfigContext'
import { useUserContext } from '~/contexts/UserContext'
import { trace } from '~/services/analyticsService'
import {
  acceptBillingInvitationAfterSignup,
  getBillingInvitationInfo,
  InvitationInfo
} from '~/services/superAdminService'
import { signup, signupUnverified } from '~/services/userService'
import { BetterToast } from '~/utils/toast'
import { usePasswordValidation } from '../../hooks/usePasswordValidation'
import { usePlanSelection } from '../../hooks/usePlanSelection'
import PasswordInput from '../common/PasswordInput'
import PasswordRequirements from './PasswordRequirements'

type SignupState = {
  email: string
  password: string
}

export const SignUpForm: FC = () => {
  const { config } = useAppConfig()
  const [isDisabled, setIsDisabled] = useState(false)
  const [formState, setFormState] = useState<SignupState>({
    email: '',
    password: ''
  })
  const [queryParams, setQueryParams] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isEmailValid, setIsEmailValid] = useState(true) // Defaults to true because an empty value is considered valid
  const { password } = formState
  const { isValid: isPasswordValid } = usePasswordValidation(password)
  const navigate = useNavigate()
  const { refetch } = useUserContext()
  const { selectedPlan } = usePlanSelection()
  const [searchParams] = useSearchParams()

  // Billing invitation state
  const billingInviteToken = searchParams.get('token')
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null)
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(!!billingInviteToken)
  const isInvitedSignup = !!(billingInviteToken && invitationInfo?.valid)

  // Fetch billing invitation info if token present
  useEffect(() => {
    if (!billingInviteToken) return

    setIsLoadingInvitation(true)
    getBillingInvitationInfo(billingInviteToken)
      .then(info => {
        setInvitationInfo(info)
        if (info.valid && info.email) {
          setFormState(prev => ({ ...prev, email: info.email! }))
        } else if (!info.valid) {
          setErrorMessage(info.error || 'Invalid or expired invitation')
        }
      })
      .catch(err => {
        console.error('Error fetching invitation:', err)
        setErrorMessage('Failed to load invitation details')
      })
      .finally(() => {
        setIsLoadingInvitation(false)
      })
  }, [billingInviteToken])

  useEffect(() => {
    if (selectedPlan && selectedPlan !== 'flex') {
      trace({ event: 'signup_exposure', labels: { plan: selectedPlan } })
    } else {
      trace('signup_exposure')
    }
  }, [selectedPlan])

  useEffect(() => {
    const url = new URL(window.location.href)
    // Remove the token from query params we'll pass along (we handle it separately)
    const params = new URLSearchParams(url.searchParams)
    params.delete('token')
    setQueryParams(params.toString())
    // Extract query params for navigation
  }, [])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage('')
    trace('signup_click')
    const { email, password } = formState
    if (!email || !password) {
      return setErrorMessage('Please fill in all fields')
    }

    // Email validation is now handled by EmailInput component

    try {
      setIsDisabled(true)

      // For invited signups, skip email verification (invitation serves as verification)
      const requireEmailOTP = isInvitedSignup ? false : config.email_verification_enabled
      if (requireEmailOTP) {
        await signupUnverified(email, password)
      } else {
        await signup(email, password, '')
      }

      // Refresh user context to get updated auth state
      await refetch()

      // If this is an invited signup, accept the billing invitation BEFORE navigating
      // This ensures the user has proper permissions when they land on the home page
      if (isInvitedSignup && billingInviteToken) {
        try {
          console.log('Accepting billing invitation after signup...')
          const result = await acceptBillingInvitationAfterSignup(billingInviteToken)
          if (result.success) {
            console.log('Billing invitation accepted successfully:', result)
            BetterToast.success(result.message || 'Welcome! You have joined the billing account.')
          } else {
            console.error('Billing invitation acceptance failed:', result)
            // Still proceed but show error
            BetterToast.error('Warning: Failed to join billing account. Please contact support.')
          }
        } catch (inviteErr) {
          console.error('Error accepting invitation after signup:', inviteErr)
          const errorMsg = inviteErr instanceof Error ? inviteErr.message : 'Unknown error'
          BetterToast.error(`Warning: ${errorMsg}. Please contact support.`)
          // Still proceed to avoid blocking the user
        }
      }

      trace({
        event: 'signup_completed',
        labels: {
          status: 'success',
          email_verification_required: requireEmailOTP.toString(),
          invited_signup: isInvitedSignup.toString(),
          ...(selectedPlan !== 'flex' && { plan: selectedPlan })
        }
      })

      BetterToast.success('Sign Up Success')

      // Refresh user context again after invitation acceptance to ensure latest permissions
      if (isInvitedSignup) {
        await refetch()
        console.log('User context refreshed after invitation acceptance')
      }

      // Always navigate to root - centralized flow decides what happens next
      const query = queryParams.length ? `?${queryParams}` : ''
      navigate('/' + query)
    } catch (err) {
      const error = err as Error
      setErrorMessage(error.message)
      trace({
        event: 'signup_completed',
        labels: {
          status: 'failed',
          reason: error.message,
          ...(selectedPlan !== 'flex' && { plan: selectedPlan })
        }
      })
    } finally {
      setIsDisabled(false)
    }
  }

  const handleInputChange =
    (field: keyof SignupState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setErrorMessage('')
      setFormState(prev => ({
        ...prev,
        [field]: e.target.value
      }))
    }

  // For invited signup, email is pre-filled and valid
  const isFormValid = formState.email && isPasswordValid && (isInvitedSignup || isEmailValid)

  // Show loading state while fetching invitation info
  if (isLoadingInvitation) {
    return (
      <div className='w-[440px] max-w-[calc(100vw-32px)] flex justify-center items-center h-auto bg-[#F9F9F9] rounded-3xl py-20'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
      </div>
    )
  }

  return (
    <div className='w-[440px] max-w-[calc(100vw-32px)] flex justify-center items-start h-auto bg-[#F9F9F9] rounded-3xl'>
      <div className='w-full px-10 py-12 flex flex-col gap-8'>
        <div>
          <h1 aria-label='Signup title' className='mb-2 text-center font-bold text-3xl select-none'>
            Sign Up
          </h1>
          <div className='margin-auto'>
            <div className='w-full flex justify-center'>
              <span className='text-lg font-medium select-none'>Have an account?</span>{' '}
              <Link href='/signin'>
                <span className='font-bold text-black select-none'>Log in</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Invitation Banner */}
        {isInvitedSignup && invitationInfo && (
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 text-center select-none'>
            <p className='text-sm text-blue-800'>
              You&apos;ve been invited to join <strong>{invitationInfo.account_name}</strong>
            </p>
            {invitationInfo.inviter_email && (
              <p className='text-xs text-blue-600 mt-1'>
                Invited by {invitationInfo.inviter_email}
              </p>
            )}
          </div>
        )}

        <form id='signupForm' onSubmit={handleSubmit}>
          <div>
            {isInvitedSignup ? (
              // Readonly email for invited signup
              <div className='relative'>
                <input
                  type='email'
                  id='email'
                  value={formState.email}
                  readOnly
                  className='w-full h-[56px] px-4 text-base bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed'
                />
                <span className='absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 select-none'>
                  From invitation
                </span>
              </div>
            ) : (
              <EmailInput
                id='email'
                placeholder='Email'
                value={formState.email}
                onChange={handleInputChange('email')}
                onValidationChange={setIsEmailValid}
                autoComplete='email'
                className='text-base'
                height='lg'
              />
            )}
          </div>
          <div className='mt-4'>
            <div className='relative'>
              <PasswordInput
                type='password'
                id='password'
                placeholder='Password'
                value={formState.password}
                onChange={handleInputChange('password')}
                autoComplete='new-password'
                height='lg'
              />
            </div>

            {errorMessage && (
              <div className='flex items-center text-red-600 text-[14px] my-[2px] mb-1 px-1 min-h-8 py-1 gap-1 select-none'>
                <IconCrossFilled className='mr-1' />
                {errorMessage}
              </div>
            )}
            {password && <PasswordRequirements password={password} />}
          </div>
          <div className='w-full flex justify-center mt-8'>
            <Button
              variant='primary'
              id='create-account'
              type='submit'
              aria-label='Sign up'
              className='w-full px-10 py-3 text-center leading-[22px] h-[46px]'
              isDisabled={isDisabled || !isFormValid}
            >
              Sign Up
            </Button>
          </div>
        </form>

        <div className='w-full text-center text-black/45 text-[13px] font-mono leading-[normal] tracking-[-0.39px] h-8 select-none'>
          <p>By signing up to create an account, you are accepting</p>
          <p>
            our <Link href='/terms'>terms of service</Link>,{' '}
            <Link href='/privacy'>privacy policy</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
